import { StackProps as CStackProps, Stack } from '@chakra-ui/react'
import { Ref, createContext, forwardRef, useContext } from 'react'

import { Skeleton } from '../../components/SkeletonLoader'
import { createWidget } from '../widget-utils'
import { StackLayout, type StackVariant } from './stackLayout'

const LEVEL_SPACING: Record<number, string> = {
  1: '32px',
  2: '24px',
  3: '16px',
  4: '8px',
  5: '4px',
  6: '2px',
}

export const resolveStackSpacing = (
  level: number,
  spacing?: StackWidgetProps['spacing'],
  gap?: StackWidgetProps['gap'],
) => spacing ?? gap ?? LEVEL_SPACING[level] ?? LEVEL_SPACING[6]

const stackContext = createContext<{ level: number }>({
  level: 1,
})

export type StackWidgetProps = CStackProps & {
  level?: keyof typeof LEVEL_SPACING
  variant?: StackVariant
  isLoading?: boolean
  loaderWatch?: string[]
  loaderHeight?: string
  loaderWidth?: string
}

export const StackWidget = forwardRef(
  (
    {
      children,
      level: inputLevel,
      variant,
      isLoading,
      loaderWatch,
      loaderHeight = '1em',
      loaderWidth = 'full',
      spacing: spacingProp,
      gap,
      ...props
    }: StackWidgetProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const { level: contextLevel } = useContext(stackContext)
    const level = inputLevel ?? contextLevel
    const stackSpacing = resolveStackSpacing(level, spacingProp, gap)

    return (
      <stackContext.Provider value={{ level: level + 1 }}>
        <Stack
          ref={ref}
          spacing={stackSpacing}
          direction="column"
          {...props}
          {...StackLayout.variant(variant)}
        >
          <Skeleton isLoading={isLoading} w={loaderWidth} h={loaderHeight}>
            {children}
          </Skeleton>
        </Stack>
      </stackContext.Provider>
    )
  },
)

export const StackWidgetDefinition = createWidget(StackWidget, {
  type: 'Stack',
})
